import React, { useState, useEffect } from "react";
import { Nav } from "react-bootstrap";
import { useLocation, useParams, Link } from "react-router-dom";
import {
    FaHome,
    FaFileContract,
    FaBolt,
    FaSignOutAlt,
    FaUserCircle,
    FaFilePdf,
    FaDatabase,
    FaTools,
    FaChevronDown,
    FaChevronRight,
    FaFire
} from "react-icons/fa";
import { connect } from "react-redux";
import { logout } from "../../redux/actions/auth";
import GestionSips from "../modals/GestionSips";
import SidebarOfferActions from "./SidebarOfferActions";
import Logo from "../../media/img/abenergia-logo.png";
import "./Sidebar.css";
import { resourceService } from "../../services/resourceService";

const Sidebar = ({ logout, user, isSuperAdmin, closeMobileSidebar, offerContext, sidebarOferta }) => {
    const location = useLocation();
    const params = useParams();
    const isCallAgent = Boolean(user?.is_call_agent);
    const isRestrictedCallAgent = isCallAgent && !isSuperAdmin;

    // Prefer offerContext if provided, otherwise fallback to URL params
    const sector_type = offerContext?.sector_type || params.sector_type;
    const oferta_id = offerContext?.oferta_id || params.oferta_id;

    // Modal States
    const [showSips, setShowSips] = useState(false);
    const [latestVersion, setLatestVersion] = useState(null);
    const [showOffersMenu, setShowOffersMenu] = useState(false);

    useEffect(() => {
        resourceService.getLatestRelease()
            .then(r => { if (r?.version) setLatestVersion(r.version); })
            .catch(() => { });
    }, []);

    useEffect(() => {
        const offerPaths = ["/contratos/ofertas", "/electrico/tarifas", "/gas/tarifas"];
        const shouldExpandOffers = offerPaths.some(path => location.pathname.startsWith(path));
        if (shouldExpandOffers) {
            setShowOffersMenu(true);
        }
    }, [location.pathname]);

    const isOfferContext = sector_type && oferta_id;

    const handleLogout = () => {
        logout();
    };

    const NavItem = ({ to, icon: Icon, label, exact = false, contextActive = false, onClick }) => {
        const isActive = exact
            ? location.pathname === to
            : to ? location.pathname.startsWith(to) && (!isOfferContext || contextActive) : false;

        return (
            <Nav.Item>
                {to ? (
                    <Nav.Link
                        as={Link}
                        to={to}
                        className={`sidebar-link ${isActive ? 'active' : ''}`}
                        onClick={closeMobileSidebar}
                    >
                        <Icon className="sidebar-icon" />
                        <span>{label}</span>
                    </Nav.Link>
                ) : (
                    <div className="sidebar-link" onClick={() => { if (onClick) onClick(); if (closeMobileSidebar) closeMobileSidebar(); }} style={{ cursor: 'pointer' }}>
                        <Icon className="sidebar-icon" />
                        <span>{label}</span>
                    </div>
                )}
            </Nav.Item>
        );
    };

    return (
        <>
            <div className="sidebar-container d-flex flex-column">

                {/* Header / Brand */}
                <div className="sidebar-header d-flex justify-content-start align-items-center">
                    <div className="brand-logo">
                        <img src={Logo} alt="AbeEnergy" className="img-fluid" style={{ maxHeight: '60px' }} />
                    </div>
                </div>

                {/* Main Navigation */}
                <div className="sidebar-content flex-grow-1">
                    <h6 className="sidebar-section-title">Menu Principal</h6>
                    <Nav className="flex-column gap-2 mb-4">
                        <NavItem to="/" icon={FaHome} label="Inicio" exact />
                        <div>
                            <Nav.Item>
                                <div
                                    className={`sidebar-link ${location.pathname.startsWith("/contratos/ofertas") || location.pathname.startsWith("/electrico/tarifas") || location.pathname.startsWith("/gas/tarifas") ? "active" : ""}`}
                                    onClick={() => setShowOffersMenu((current) => !current)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <FaFileContract className="sidebar-icon" />
                                    <span className="flex-grow-1">Ofertas</span>
                                    {showOffersMenu ? <FaChevronDown className="ms-auto" /> : <FaChevronRight className="ms-auto" />}
                                </div>
                            </Nav.Item>

                            {showOffersMenu && (
                                <div className="sidebar-submenu animate-fade-in">
                                    <NavItem to="/contratos/ofertas" icon={FaFileContract} label="Ver ofertas" contextActive={true} />
                                    <NavItem to="/electrico/tarifas" icon={FaBolt} label="Nuevo contrato luz" contextActive={true} />
                                    <NavItem to="/gas/tarifas" icon={FaFire} label="Nuevo contrato gas" contextActive={true} />
                                </div>
                            )}
                        </div>
                        {!isRestrictedCallAgent && (
                            <>
                                <NavItem to="/contratos/contratos" icon={FaFileContract} label="Contratos" />
                            </>
                        )}
                        <>
                            <NavItem to="/recursos" icon={FaFilePdf} label="Recursos" />
                        </>
                    </Nav>



                    {!isRestrictedCallAgent && (
                        <>
                            <h6 className="sidebar-section-title">Herramientas</h6>
                            <Nav className="flex-column gap-2">
                                <NavItem icon={FaDatabase} label="SIPS" onClick={() => setShowSips(true)} />
                            </Nav>
                        </>
                    )}

                    {!isRestrictedCallAgent && isSuperAdmin && (
                        <>
                            <h6 className="sidebar-section-title mt-4">Administración</h6>
                            <Nav className="flex-column gap-2">
                                <NavItem to="/admin/recursos" icon={FaTools} label="Gestionar Recursos" />
                            </Nav>
                        </>
                    )}

                    {/* Offer Context Actions */}
                    {isOfferContext && sidebarOferta && (
                        <SidebarOfferActions
                            oferta={sidebarOferta}
                            onRefresh={() => window.location.reload()}
                        />
                    )}
                </div>

                {/* Footer / User Profile */}
                <div className="sidebar-footer mt-auto">
                    <div className="user-profile d-flex align-items-center gap-2 p-2 rounded transition-all">
                        <Link to="/profile" className="d-flex align-items-center gap-2 text-decoration-none text-dark flex-grow-1">
                            <FaUserCircle className="user-avatar fs-2 text-secondary" />
                            <div className="user-info d-flex flex-column" style={{ lineHeight: '1.2' }}>
                                <span className="user-name fw-bold" style={{ fontSize: '0.9rem' }}>{user ? user.first_name : 'Usuario'}</span>
                                <span className="user-role text-muted" style={{ fontSize: '0.75rem' }}>Ver Perfil</span>
                            </div>
                        </Link>
                        <button
                            className="logout-btn border-0 bg-transparent p-2 rounded-circle text-danger hover-bg-danger-light transition-all"
                            title="Cerrar Sesión"
                            onClick={handleLogout}
                        >
                            <FaSignOutAlt />
                        </button>
                    </div>

                    {latestVersion && (
                        <div
                            className="px-2 pt-2 mt-2"
                            style={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}
                        >
                            <Link
                                to="/novedades"
                                className="d-block text-muted text-decoration-none text-center"
                                style={{ fontSize: '0.7rem' }}
                                onClick={closeMobileSidebar}
                            >
                                v{latestVersion} - Ver novedades
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <GestionSips show={showSips} close={setShowSips} />
        </>
    );
};

const mapStateToProps = (state) => ({
    user: state.Auth.user,
    isSuperAdmin: state.Auth.isSuperAdmin
});

export default connect(mapStateToProps, { logout })(Sidebar);
